import { surahFromAudioUrl } from "@/lib/utils";
import type { Word } from "@/lib/types";

export function groupWordsToLines(arr: Word[]) {
  let finalArr: Word[][] = [];
  // every word obj has an line_number item
  // make an array containing only line numbers, remove duplicates
  // find out how many lines are there on the page
  const linesArr = arr.map((word) => word.line_number);
  const linesOnPage = new Set(linesArr);

  // iterating over set
  linesOnPage.forEach((item) => {
    const filtered = arr.filter((word) => word.line_number === item);
    finalArr.push(filtered);
  });

  return finalArr;
}

/**
 * A line's words in reading order. Copies first: the lines come from loader
 * data, which must never be sorted in place during render.
 */
export function sortLineWords(line: Word[]) {
  return [...line].sort((a, b) => a.id - b.id);
}

/**
 * The surah that starts on this line (its first word is ayah 1, word 1:
 * "wbw/002_001_001.mp3"), or null. Takes the line in reading order.
 */
export function surahStartingOnLine(line: Word[]) {
  const first = line[0];
  if (first?.audio_url?.slice(-11, -4) !== "001_001") return null;
  return surahFromAudioUrl(first.audio_url);
}

/**
 * Classes for a block of mushaf lines: the fluid size (lines never wrap, the
 * longest is 14.6em) and the line rhythm. Pages 1-2 have short lines (9.4em),
 * so they read larger. Compose with a plain string, not cn(): tailwind-merge
 * reads "text-mushaf" as a text colour and drops it next to text-ink.
 */
export function mushafTextClass(pageNumber: number) {
  return pageNumber <= 2
    ? "text-mushaf space-y-[0.42em] [--text-mushaf:clamp(1.25rem,calc((100cqi-1.5rem)/10.6),2.25rem)]"
    : "text-mushaf space-y-[0.42em]";
}

export function removeFootnotes(text: string) {
  return text.replace(/<sup foot_note=\d+>.*?<\/sup>/g, "");
}

// Word-link highlight fills, drawn with mix-blend-multiply on the cream page.
// Same hues as before, retuned for paper-100 (the old yellow was lighter than
// the paper). Hex on purpose: Tailwind drops theme vars used only from inline
// styles, so var(--color-hl-*) would not resolve here.
export const TEXT_COLORS = new Map<string, string>();
TEXT_COLORS.set("YELLOW", "#fcedae");
TEXT_COLORS.set("ORANGE", "#fbd6ac");
TEXT_COLORS.set("GREEN", "#d3edcf");
TEXT_COLORS.set("BLUE", "#d3e8f6");
TEXT_COLORS.set("RED", "#f8cdd3");

export const COLORS_TRANSLATION = new Map<string, string>();
COLORS_TRANSLATION.set("YELLOW", "Žuta");
COLORS_TRANSLATION.set("ORANGE", "Narandžasta");
COLORS_TRANSLATION.set("GREEN", "Zelena");
COLORS_TRANSLATION.set("BLUE", "Plava");
COLORS_TRANSLATION.set("RED", "Crvena");

export function printPdf() {
  if (typeof window === "undefined") return;

  return window.print();
}
